"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  getMeasurementId,
  trackFeatureView,
  trackPageView,
} from "../../lib/analytics";

/**
 * Maps route patterns to product-feature names for `feature_view` events.
 *
 * Keep this list explicit rather than computed: every entry represents an
 * intentional engagement surface that the product team cares about.
 */
const FEATURE_ROUTE_PATTERNS: ReadonlyArray<{
  pattern: RegExp;
  feature: string;
}> = [
  { pattern: /^\/$/, feature: "lab" },
  { pattern: /^\/lab(\/|$)/, feature: "lab" },
  { pattern: /^\/brain-visualizer(\/|$)/, feature: "brain_visualizer" },
  { pattern: /^\/configurator(\/|$)/, feature: "configurator" },
  { pattern: /^\/embodiment(\/|$)/, feature: "embodiment" },
  { pattern: /^\/embodiments(\/|$)/, feature: "embodiments_catalog" },
  { pattern: /^\/new-experiment(\/|$)/, feature: "new_experiment" },
  { pattern: /^\/play(\/|$)/, feature: "play" },
  { pattern: /^\/hub(\/|$)/, feature: "hub" },
  { pattern: /^\/pricing(\/|$)/, feature: "pricing" },
  { pattern: /^\/nrs\/download(\/|$)/, feature: "nrs_download_page" },
];

export function featureNameForPath(path: string): string | null {
  for (const entry of FEATURE_ROUTE_PATTERNS) {
    if (entry.pattern.test(path)) return entry.feature;
  }
  return null;
}

/**
 * Client-side page_view emitter for the Next.js App Router.
 *
 * GA4 is configured with `send_page_view: false` in GoogleAnalytics.tsx so we
 * have full control here and avoid double-counting with Enhanced Measurement's
 * history-change auto-detection.
 *
 * Also fires `feature_view` for known product surfaces so engagement
 * reporting works independently of page_view.
 *
 * `/server-down` is intentionally included in page_view. Excluding it would
 * hide outages. It is not a product feature, so it does not emit feature_view.
 */
export default function PageViewTracker() {
  const measurementId = getMeasurementId();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTrackedKey = useRef<string | null>(null);

  useEffect(() => {
    if (!measurementId) return;
    if (typeof window === "undefined") return;
    if (pathname == null) return;

    const query = searchParams?.toString() ?? "";
    const path = query ? `${pathname}?${query}` : pathname;
    if (lastTrackedKey.current === path) return;
    lastTrackedKey.current = path;

    trackPageView({
      path,
      title: typeof document !== "undefined" ? document.title : undefined,
      location: window.location.href,
    });

    const feature = featureNameForPath(pathname);
    if (feature) {
      trackFeatureView(feature);
    }
  }, [measurementId, pathname, searchParams]);

  return null;
}
